/**
 * CrazyGames adapter.
 *
 * Why an adapter at all: ToyRaiders' loop is "看到朋友中招 → 点同一间小屋 →
 * 亲自挑战", and on CrazyGames that loop is *their* invite-link + gameplay-event
 * plumbing. But the game must also run identically on toyraiders.pajama.studio,
 * in a local dev server, and in a test — so every platform capability is
 * accessed through this file and degrades to a working no-op.
 *
 * IMPORTANT — verify before submission. The SDK surface below is called
 * defensively (feature-detected, wrapped, never awaited without a timeout)
 * precisely because we must not hard-depend on a shape we have not re-read.
 * Check every call against https://docs.crazygames.com/sdk/ before shipping a
 * build to the portal, and keep the guards even after you do: the portal loads
 * the SDK from their CDN, and a CDN that fails must not take the game with it.
 */

const SDK_URL = 'https://sdk.crazygames.com/crazygames-sdk-v3.js'
const LOAD_TIMEOUT_MS = 4000

export interface PlatformUser {
  id: string
  name: string
  /** True when the identity came from the portal rather than a local guest id. */
  verified: boolean
}

type AdKind = 'midgame' | 'rewarded'

interface CrazySdkLike {
  init?: () => Promise<void>
  game?: {
    gameplayStart?: () => void
    gameplayStop?: () => void
    happytime?: () => void
    inviteLink?: (params: Record<string, string>) => Promise<string> | string
    showInviteButton?: (params: Record<string, string>) => void
    hideInviteButton?: () => void
    getInviteParam?: (name: string) => string | null
    inviteParam?: (name: string) => string | null
  }
  user?: {
    getUser?: () => Promise<{ username?: string; profilePictureUrl?: string } | null>
  }
  ad?: {
    requestAd?: (
      kind: AdKind,
      callbacks: {
        adFinished?: () => void
        adError?: (err: unknown) => void
        adStarted?: () => void
      },
    ) => void
  }
}

declare global {
  interface Window {
    CrazyGames?: { SDK?: CrazySdkLike }
  }
}

/** True when we should even try: the portal domain, or an explicit `?cg=1`. */
function shouldUseSdk(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(location.search)
  if (params.get('cg') === '1') return true
  if (params.get('cg') === '0') return false
  return /crazygames|1001juegos|crazy-?games/i.test(location.hostname + document.referrer)
}

function loadScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const el = document.createElement('script')
    el.src = url
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('sdk load failed'))
    document.head.appendChild(el)
    setTimeout(() => reject(new Error('sdk load timeout')), LOAD_TIMEOUT_MS)
  })
}

function guestId(): string {
  const key = 'toyraiders.guestId'
  const existing = localStorage.getItem(key)
  if (existing) return existing
  // No Math.random in gameplay code; identity is not gameplay, but keep it
  // boring and collision-resistant anyway.
  const id = `g_${Date.now().toString(36)}_${crypto.randomUUID().slice(0, 8)}`
  localStorage.setItem(key, id)
  return id
}

class Platform {
  private sdk: CrazySdkLike | null = null
  private ready: Promise<void> | null = null
  /** True once we know the portal SDK is genuinely usable. */
  available = false

  init(): Promise<void> {
    if (this.ready) return this.ready
    this.ready = (async () => {
      if (!shouldUseSdk()) return
      try {
        await loadScript(SDK_URL)
        const sdk = window.CrazyGames?.SDK
        if (!sdk) return
        await sdk.init?.()
        this.sdk = sdk
        this.available = true
      } catch {
        // Portal unreachable or blocked: the game is fully playable without it.
        this.sdk = null
        this.available = false
      }
    })()
    return this.ready
  }

  /** Bracket actual play so the portal knows when not to interrupt. */
  gameplayStart(): void {
    try {
      this.sdk?.game?.gameplayStart?.()
    } catch {
      /* portal-side hiccup must never break a raid */
    }
  }

  gameplayStop(): void {
    try {
      this.sdk?.game?.gameplayStop?.()
    } catch {
      /* ignore */
    }
  }

  /** A genuinely delightful beat — the portal uses it to time its own moments. */
  happy(): void {
    try {
      this.sdk?.game?.happytime?.()
    } catch {
      /* ignore */
    }
  }

  /**
   * A link that drops the recipient into *this room*. On the portal we ask the
   * SDK so the referral is attributed; everywhere else a plain hash URL does
   * exactly the same job, which is why the viral loop needs no backend.
   */
  async inviteLink(params: Record<string, string>, fallbackUrl: string): Promise<string> {
    try {
      const link = this.sdk?.game?.inviteLink?.(params)
      const resolved = link instanceof Promise ? await link : link
      if (typeof resolved === 'string' && resolved.length > 0) return resolved
    } catch {
      /* fall through */
    }
    return fallbackUrl
  }

  /** Room code the player was invited into, if any. */
  invitedRoom(): string | null {
    try {
      const g = this.sdk?.game
      const value = g?.getInviteParam?.('room') ?? g?.inviteParam?.('room') ?? null
      return value && value.length > 0 ? value : null
    } catch {
      return null
    }
  }

  async user(): Promise<PlatformUser> {
    try {
      const u = await this.sdk?.user?.getUser?.()
      if (u?.username) return { id: `cg_${u.username}`, name: u.username, verified: true }
    } catch {
      /* fall through to guest */
    }
    return { id: guestId(), name: '', verified: false }
  }

  /**
   * Rewarded ad. Resolves true only when the reward was genuinely earned, so
   * callers can treat `false` as "give them nothing, silently".
   */
  requestAd(kind: AdKind): Promise<boolean> {
    const req = this.sdk?.ad?.requestAd
    if (!req) return Promise.resolve(false)
    return new Promise((resolve) => {
      let settled = false
      const done = (ok: boolean) => {
        if (settled) return
        settled = true
        resolve(ok)
      }
      try {
        req.call(this.sdk!.ad, kind, {
          adFinished: () => done(true),
          adError: () => done(false),
        })
      } catch {
        done(false)
      }
      setTimeout(() => done(false), 30000)
    })
  }
}

export const platform = new Platform()
